import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, ArrowUpRight, Loader2, Quote } from 'lucide-react';
import { getBlogPost } from '@/lib/api';
import SEO from '@/components/SEO';

// Stable key for a list item within a block — uses content prefix when available.
const stableItemKey = (item, fallbackIdx) => {
  if (typeof item !== 'string') return `item-${fallbackIdx}`;
  // Hash-ish: short content slice + length keeps it deterministic & unique enough for SSR.
  return `item-${item.length}-${item.slice(0, 24).replace(/\s+/g, '-')}`;
};

const BlockRenderer = ({ block }) => {
  if (!block) return null;
  switch (block.type) {
    case 'h3':
      return (
        <h3 className="font-display text-xl sm:text-2xl font-[650] text-white mt-10 mb-3 tracking-tight">
          {block.content}
        </h3>
      );
    case 'list':
      return (
        <ul className="my-5 space-y-2.5">
          {(block.content || []).map((item, i) => (
            <li key={stableItemKey(item, i)} className="flex gap-3 text-white/80 leading-relaxed">
              <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[rgb(var(--brand-electric))]" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'quote':
      return (
        <blockquote className="my-8 relative rounded-2xl border-l-2 border-[rgb(var(--brand-electric))] bg-white/[0.03] pl-6 pr-5 py-5">
          <Quote size={18} className="absolute -top-2 left-4 text-[rgb(var(--brand-electric))] bg-[rgb(var(--bg-0))] px-1" />
          <p className="font-display text-lg sm:text-xl text-white/90 leading-relaxed italic">
            “{block.content}”
          </p>
        </blockquote>
      );
    case 'p':
    default:
      return (
        <p className="my-4 text-white/80 leading-relaxed text-[15.5px]">
          {block.content}
        </p>
      );
  }
};

// Build a stable, deterministic key for a block from its content.
const blockKey = (block, idx) => {
  if (!block) return `b-${idx}`;
  if (block.type === 'list') {
    const first = Array.isArray(block.content) ? (block.content[0] || '') : '';
    return `${block.type}-${idx}-${String(first).slice(0, 16)}`;
  }
  return `${block.type}-${idx}-${String(block.content || '').slice(0, 16)}`;
};

const BlogDetailPage = ({ lang }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setPost(null);
    window.scrollTo({ top: 0, behavior: 'instant' });
    getBlogPost(slug, lang)
      .then((data) => { if (active) setPost(data); })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, lang]);

  if (loading) {
    return (
      <main className="min-h-[60vh] grid place-items-center" data-testid="blog-detail-loading">
        <div className="text-white/60 flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> {lang === 'es' ? 'Cargando…' : 'Loading…'}
        </div>
      </main>
    );
  }

  if (notFound || !post) {
    return (
      <main className="min-h-[60vh] grid place-items-center px-6" data-testid="blog-detail-404">
        <div className="text-center">
          <div className="kicker-mono text-white/60 mb-2">404</div>
          <h1 className="font-display text-3xl font-[650] text-white">
            {lang === 'es' ? 'Artículo no encontrado' : 'Post not found'}
          </h1>
          <button onClick={() => navigate('/#blog')} className="btn-primary mt-6 inline-flex" data-testid="blog-detail-404-back">
            <ArrowLeft size={14} /> {lang === 'es' ? 'Volver al blog' : 'Back to blog'}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-detail-page">
      <SEO
        lang={lang}
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.excerpt}
        image={post.cover}
        type="article"
      />

      {/* Hero */}
      <section className="bg-grid relative pt-12 pb-10 sm:pt-16 sm:pb-12">
        <div className="orb float-slow" style={{ width: 360, height: 360, background: 'rgba(5,126,243,.30)', top: '-100px', right: '-80px' }} />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/#blog')}
            className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="blog-detail-back"
          >
            <ArrowLeft size={14} /> {lang === 'es' ? 'Volver al blog' : 'Back to blog'}
          </button>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="chip"><span className="dot" />{post.category}</span>
            <span className="text-[11px] font-mono-accent uppercase tracking-wider text-white/50 inline-flex items-center gap-1">
              <Calendar size={12} /> {post.date}
            </span>
            <span className="text-[11px] font-mono-accent uppercase tracking-wider text-white/50 inline-flex items-center gap-1">
              <Clock size={12} /> {post.read_minutes} min
            </span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-[700] tracking-tight text-white leading-[1.05]"
            data-testid="blog-detail-title"
          >
            {post.title}
          </motion.h1>

          <p className="mt-4 text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl">
            {post.excerpt}
          </p>
        </div>
      </section>

      {/* Cover */}
      {post.cover && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[16/8] sm:aspect-[16/7] bg-[rgb(var(--bg-1))]">
            <img src={post.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--bg-0))]/40 via-transparent to-transparent" />
          </div>
        </div>
      )}

      {/* Body */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-10" data-testid="blog-detail-body">
        {(post.body || []).map((b, i) => (
          <BlockRenderer key={blockKey(b, i)} block={b} />
        ))}
      </article>

      {/* Related + CTA */}
      <section className="border-t border-white/5 py-14 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="kicker-mono mb-3">// {lang === 'es' ? 'También podría interesarte' : 'You might also like'}</div>
          <div className="grid sm:grid-cols-2 gap-5 mt-4">
            {(post.related || []).map((p, i) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-[rgb(var(--bg-1))] hover:border-[rgba(5,126,243,.5)] transition-colors flex"
                data-testid={`blog-related-${i}`}
              >
                <div className="w-32 sm:w-40 shrink-0 bg-[rgb(var(--bg-2))] overflow-hidden">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition duration-500" />
                </div>
                <div className="p-4 flex flex-col justify-between min-w-0">
                  <div>
                    <div className="text-[10px] font-mono-accent uppercase tracking-wider text-white/50">{p.category}</div>
                    <h4 className="mt-1 font-display text-base font-[650] text-white leading-tight line-clamp-2">{p.title}</h4>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[rgb(var(--brand-electric))]">
                    {lang === 'es' ? 'Leer' : 'Read'} <ArrowUpRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-[rgb(var(--bg-1))] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="kicker-mono text-white/60">// {lang === 'es' ? '¿Hablamos confidencialmente?' : 'Talk to us confidentially'}</div>
              <h4 className="font-display text-xl font-[650] text-white mt-1">
                {lang === 'es' ? 'Agenda una reunión NDA-ready' : 'Schedule an NDA-ready meeting'}
              </h4>
            </div>
            <Link to="/agenda" className="btn-primary" data-testid="blog-detail-cta">
              {lang === 'es' ? 'Agendar' : 'Book now'} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogDetailPage;
